import { format } from "date-fns";
import { Schedule } from "@/types/branch";
import { ServicesConfig } from "@/types";

const daysOfWeekFull = [
  "Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"
];

const daysOfWeekAbbr = [
  "Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"
];

const dayKeyToIdx: Record<string, number> = {
  sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6

};

// Get days where closed is available
export const availableDaysIdx = (schedule: Schedule | null | undefined): number[] => {
  if (!schedule) return [];
  return Object.keys(schedule)
    .filter(day => !schedule[day as keyof Schedule].closed)
    .map(day => dayKeyToIdx[day])
    .filter(idx => idx !== undefined)
    .sort((a, b) => a - b);
};

// Format availability into a human-readable string
export const formatAvailability = (
  schedule?: Schedule | null
) => {

  if (!schedule) return "";

  const availableIdx = availableDaysIdx(schedule);

  // If there are no days return
  if (availableIdx.length === 0) return "";

  // Check if available (full week)
  if (availableIdx.length === 7 && availableIdx[0] === 0 && availableIdx[6] === 6) {
    return "Lunes a Domingo";
  }

  // Check if available (office days: Monday to Friday)
  if (
    availableIdx.length === 5 &&
    availableIdx[0] === 1 &&
    availableIdx[4] === 5 &&
    availableIdx.every((val, idx) => val === idx + 1)
  ) {
    return "Lunes a Viernes";
  }


  // If two days, use full names
  if (availableIdx.length === 2) {
    // If Domingo (Sunday) is one of the days, always put it last
    if (availableIdx.includes(0)) {
      const otherIdx = availableIdx.find(idx => idx !== 0);
      return `${daysOfWeekFull[otherIdx]} y ${daysOfWeekFull[0]}`;
    }
    return `${daysOfWeekFull[availableIdx[0]]} y ${daysOfWeekFull[availableIdx[1]]}`;
  }

  // If more than two, use abbreviations
  if (availableIdx.length > 2) {
    const sortedIdx = availableIdx.filter(idx => idx !== 0).concat(
      availableIdx.includes(0) ? [0] : []
    );
    const abbrDays = sortedIdx.map(idx => daysOfWeekAbbr[idx]);
    const lastDay = abbrDays.pop();
    return abbrDays.length
      ? abbrDays.join(", ") + " y " + lastDay
      : lastDay;
  }

  // If only one day, use full name
  return daysOfWeekFull[availableIdx[0]];
};

// Check if service is available today
export const isAvailableToday = (schedule?: Schedule | null) => {
  if (!schedule) return true;
  const today = format(new Date(), "eee").toLowerCase();

  const availableDays = Object.entries(schedule)
    .filter(([_, dayOptions]) => !dayOptions.closed)
    .map(([dayKey]) => dayKey);

  return availableDays.includes(today);
};

// Check if service is available now
export const checkServiceAvailability = (
  serviceConfig: ServicesConfig | undefined,
  date: Date = new Date()
): boolean => {
  if (!serviceConfig) {
    return false;
  }

  // First, check if the service is enabled
  if (!serviceConfig.isEnabled) {
    return false;
  }

  // If no schedule is defined, consider it always available
  if (!serviceConfig.schedule) {
    return true;
  }
  // Helper to convert "HH:mm" to total minutes
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  // Get today's schedule
  const today = format(date, "eee").toLowerCase();
  const daySchedule = serviceConfig.schedule[today];

  if (!daySchedule || daySchedule.closed === true) {
    return false;
  }

  // Check if current time is within any of the open hours
  const currentTotalMinutes = timeToMinutes(format(date, "HH:mm"));

  const openHoursToday = daySchedule.openHours || [];

  // Check if current time falls within any open hours
  const isWithinOpenHours = openHoursToday.some(({ start, end }) => {
    const startTotalMinutes = timeToMinutes(start);
    const endTotalMinutes = timeToMinutes(end);

    return currentTotalMinutes >= startTotalMinutes && currentTotalMinutes < endTotalMinutes;
  });

  if (!isWithinOpenHours) {
    return false;
  }
  return true;
};